import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DownloadIcon from '@mui/icons-material/Download'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { Box, Button, Typography } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import I18n from 'i18n-js'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Loader, SnackBar } from '../../components'
import { AuthLayout } from '../../components/authLayout'
import FormTwoFactor from '../../components/formTwoFactor'
import {
  requestSecureDocumentOtp,
  validateSecureDocumentOtpAndGetPdf,
  validateSecureDocumentUuid,
} from '../../services/secureDocumentsPublic'
import { downloadPdfFromBlob } from '../../utils/tools'

const RESEND_COOLDOWN_SECONDS = 20

const STATE = {
  VALIDATING_UUID: 'validating_uuid',
  UUID_VALID: 'uuid_valid',
  UUID_INVALID: 'uuid_invalid',
  REQUESTING_OTP: 'requesting_otp',
  VALIDATING_OTP: 'validating_otp',
  OTP_INVALID: 'otp_invalid',
  PDF_READY: 'pdf_ready',
  PDF_LOADING: 'pdf_loading',
  PDF_ERROR: 'pdf_error',
}

/** @typedef {{ valid: boolean, recipient_type: string, phone_hint: string }} ValidationData */

export default function SecureAccess() {
  const { uuid } = useParams()
  const isMobile = useMediaQuery('(max-width:960px)')
  const [viewState, setViewState] = useState(STATE.VALIDATING_UUID)
  const [otpError, setOtpError] = useState(null)
  const [otpLoading, setOtpLoading] = useState(false)
  const [validationData, setValidationData] = useState(/** @type {ValidationData | null} */ (null))
  const [resendCooldown, setResendCooldown] = useState(0)
  const [isRequestingOtp, setIsRequestingOtp] = useState(false)

  const [pdfData, setPdfData] = useState(null)
  const [pdfObjectUrl, setPdfObjectUrl] = useState(null)

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' })
  const cooldownIntervalRef = useRef(null)

  const sendOtpRequest = useCallback(async () => {
    if (!uuid) return false
    const { error } = await requestSecureDocumentOtp(uuid)
    if (error) return false
    setResendCooldown(RESEND_COOLDOWN_SECONDS)
    setSnackbar({ open: true, message: I18n.t('secureAccess.otpSent'), severity: 'success' })
    return true
  }, [uuid])

  useEffect(() => {
    if (!uuid) {
      setViewState(STATE.UUID_INVALID)
      return
    }
    let cancelled = false
    ;(async () => {
      const { data, error } = await validateSecureDocumentUuid(uuid)
      if (cancelled) return
      if (error || !data || !data.valid) {
        setViewState(STATE.UUID_INVALID)
        return
      }
      setValidationData(data)
      setViewState(STATE.UUID_VALID)
      if (cancelled) return
      await sendOtpRequest()
    })()
    return () => { cancelled = true }
  }, [uuid])

  useEffect(() => {
    if (resendCooldown <= 0) {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current)
        cooldownIntervalRef.current = null
      }
      return
    }
    cooldownIntervalRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownIntervalRef.current) {
            clearInterval(cooldownIntervalRef.current)
            cooldownIntervalRef.current = null
          }
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current)
        cooldownIntervalRef.current = null
      }
    }
  }, [resendCooldown])

  useEffect(() => {
    if (!isMobile || !pdfData) return
    if (pdfData.type === 'blob' && pdfData.blob) {
      downloadPdfFromBlob(pdfData.blob, I18n.t('secureAccess.pdfFilename'))
    } else if (pdfData.type === 'url' && pdfData.url) {
      window.open(pdfData.url, '_blank', 'noopener,noreferrer')
    }
  }, [pdfData, isMobile])

  const handleResendOtp = useCallback(async () => {
    if (resendCooldown > 0) return
    setIsRequestingOtp(true)
    await sendOtpRequest()
    setIsRequestingOtp(false)
  }, [resendCooldown, sendOtpRequest])

  const handleSubmitOtp = useCallback(async (code) => {
    if (!code || code.length !== 6) return
    setOtpLoading(true)
    setOtpError(null)
    const { data, error } = await validateSecureDocumentOtpAndGetPdf(uuid, { code })
    setOtpLoading(false)
    if (error || !data) {
      setOtpError(I18n.t('secureAccess.otpInvalid'))
      return
    }
    setOtpError(null)
    let objectUrl = null
    if (data instanceof Blob) {
      objectUrl = URL.createObjectURL(data)
      setPdfData({ type: 'blob', blob: data, objectUrl })
    } else if (data?.url) {
      setPdfData({ type: 'url', url: data.url })
    } else if (data?.content) {
      const blob = base64ToBlob(data.content)
      objectUrl = URL.createObjectURL(blob)
      setPdfData({ type: 'blob', blob, objectUrl })
    } else {
      setOtpError(I18n.t('secureAccess.pdfError'))
      return
    }
    setPdfObjectUrl(objectUrl)
  }, [uuid])

  const handleOtpChange = useCallback((e) => {
    const value = e?.target?.value ?? ''
    setOtpError(null)
    if (value.length === 6) {
      handleSubmitOtp(value)
    }
  }, [handleSubmitOtp])

  if (viewState === STATE.VALIDATING_UUID) {
    return (
      <AuthLayout loading>
        <Loader />
      </AuthLayout>
    )
  }

  if (viewState === STATE.UUID_INVALID) {
    return (
      <AuthLayout>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            minHeight: '100%',
            py: 4,
            px: 2,
          }}
        >
          <ErrorOutlineIcon
            sx={{
              fontSize: 56,
              color: 'error.main',
              mb: 2,
            }}
            aria-hidden
          />
          <Typography
            color="error"
            variant="h6"
            component="p"
            sx={{
              fontWeight: 600,
              mb: 1.5,
              maxWidth: 320,
            }}
          >
            {I18n.t('secureAccess.linkInvalid')}
          </Typography>
          <Typography
            color="text.secondary"
            variant="body2"
            sx={{
              maxWidth: 320,
              lineHeight: 1.5,
            }}
          >
            {I18n.t('secureAccess.linkInvalidDescription')}
          </Typography>
        </Box>
      </AuthLayout>
    )
  }

  const pdfSrc = pdfData?.url || pdfData?.objectUrl || pdfObjectUrl

  if (pdfData && pdfSrc) {
    if (isMobile) {
      return (
        <AuthLayout title={I18n.t('secureAccess.pdfReadyTitle')}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: 2, gap: 2 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'success.main' }} />
            <Typography variant="body2" color="text.secondary">
              {I18n.t('secureAccess.pdfReadyDescription')}
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              sx={{ textTransform: 'none' }}
              startIcon={<DownloadIcon />}
              onClick={() => {
                if (pdfData.type === 'blob' && pdfData.blob) {
                  downloadPdfFromBlob(pdfData.blob, I18n.t('secureAccess.pdfFilename'))
                } else if (pdfData.url) {
                  window.open(pdfData.url, '_blank', 'noopener,noreferrer')
                }
              }}
            >
              {I18n.t('secureAccess.pdfDownloadAgain')}
            </Button>
          </Box>
        </AuthLayout>
      )
    }
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%',
          bgcolor: 'background.paper',
          zIndex: 1300,
        }}
      >
        <Box
          component="iframe"
          src={pdfSrc}
          title={I18n.t('secureAccess.pdfModalTitle')}
          sx={{
            display: 'block',
            width: '100%',
            height: '100%',
            border: 'none',
          }}
        />
        <SnackBar
          text={snackbar.message}
          severity={snackbar.severity}
          show={snackbar.open}
          HandleShow={() => setSnackbar((s) => ({ ...s, open: false }))}
          time={3000}
        />
      </Box>
    )
  }

  return (
    <AuthLayout title={I18n.t('secureAccess.title')}>
      {validationData?.phone_hint && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: 'center', mb: 2 }}
        >
          {I18n.t('secureAccess.phoneHint', { hint: validationData.phone_hint })}
        </Typography>
      )}
      <FormTwoFactor
        publicMode
        onRequestCode={handleResendOtp}
        requestCodeLabel={
          resendCooldown > 0
            ? I18n.t('secureAccess.requestCodeAgainIn')
            : I18n.t('secureAccess.requestCodeAgain')
        }
        stopWatch={resendCooldown}
        resendDisabled={resendCooldown > 0 || isRequestingOtp}
        change={handleOtpChange}
        submit={handleSubmitOtp}
        errMsj={{ error: !!otpError, message: otpError ? 'secureAccess.otpInvalid' : '' }}
        disabled={otpLoading}
        firstTime={false}
        subtitleLabel={I18n.t('secureAccess.enterCode')}
      />

      <SnackBar
        text={snackbar.message}
        severity={snackbar.severity}
        show={snackbar.open}
        HandleShow={() => setSnackbar((s) => ({ ...s, open: false }))}
        time={3000}
      />
    </AuthLayout>
  )
}

function base64ToBlob(base64) {
  const byteCharacters = atob(base64)
  const byteNumbers = new Array(byteCharacters.length)
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i)
  }
  const byteArray = new Uint8Array(byteNumbers)
  return new Blob([byteArray], { type: 'application/pdf' })
}
